// ===============================================================
// AccountSessionStateContext.js — single, canonical account-access +
// gender-theme resolution, mounted once beneath AuthProvider/Router.
// ===============================================================
//
// Consolidates two previously-independent Firestore reads of the exact
// same users/{uid} document - RequireGender's own access/redirect check
// and GenderThemeContext's own gender-theme resolution - into ONE read,
// so navigating between guarded routes (which used to remount
// RequireGender and re-run its own getDoc on every navigation) no longer
// re-fetches at all: this provider is mounted once, above the route
// tree, and never unmounts for the lifetime of a signed-in session.
//
// RequireGender.js and GenderThemeContext.js both now consume this
// context instead of performing their own reads - see each file for how
// they map this shared state onto their own existing, unchanged public
// behavior (RequireGender's redirects; GenderThemeContext's DOM
// data-theme attribute write).
//
// Every check below is the exact same check RequireGender.js has always
// performed (accountStatus pending_delete/suspended, missing gender,
// developer-claim bypass, female verification-status allow-list) - same
// order, same Firestore field names, same claim name - only the
// destination (blocked/access/reason instead of a direct navigate call)
// differs.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "./AuthContext";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { getIdTokenResult } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { detectUserCountry } from "../utils/geo";

// Bounded, client-side-only background retry for a user with no
// realCountry yet - starts as soon as an authenticated uid with no
// realCountry is seen (including with no gender set yet - i.e.
// throughout the ENTIRE incomplete-onboarding window, not only after
// completeOnboarding). One immediate attempt, then up to 3 more at
// 5s/15s/45s - 4 total attempts per authenticated session, at most,
// stopping ONLY on: success, the server reporting alreadyLocked, or a
// session boundary (logout/uid change/unmount/new generation). EVERY
// unresolved detection outcome - timeout, rate_limited, client_error,
// server_error, provider_error, invalid_response, unknown_country,
// network_error, or an unexpected rejection - spends exactly one of the
// 4 bounded attempts and is otherwise treated identically: there is no
// "non-retryable status" carve-out, by explicit design. The scheduling
// state itself lives in countryRetryRef/sessionGenerationRef below (in
// the component), so it survives re-renders without causing any; this
// module-level function is the pure, stateless lookup+save only.
//
// CLIENT-REPORTED, NOT SERVER-VERIFIED: each attempt performs the GeoIP
// lookup itself, directly from this browser/WebView (see
// src/utils/geo.js's detectUserCountry()), then - only on a valid
// result - persists it through the authenticated
// functions/auth/reportClientCountry.js callable, the ONLY country-
// resolution callable in this codebase. See that file's header for the
// full trust-boundary explanation - this value must never be used for
// authorization, payments, or any other security-relevant decision.
//
// Deliberately never AWAITED by resolveAccountState: every failure mode
// here (including the lookup or save being slow or unavailable) must
// never delay this provider's own loading/access resolution, and so
// never delay navigation, login, or profile loading either. Never
// invoked at all for any user who already has a locked realCountry.
const COUNTRY_RETRY_DELAYS_MS = [5000, 15000, 45000];

/**
 * Performs exactly one lookup+save attempt and classifies the outcome
 * into a {resolved, alreadyLocked, retryable} shape the bounded
 * scheduler below uses to decide whether to keep going.
 *
 * CANCELLATION: `signal` is the scheduler's own AbortController's
 * signal for THIS attempt (see runCountryDetectionAttempt below) -
 * passed straight through to detectUserCountry() so a logout/uid-
 * change/unmount can abort the actual in-flight fetch immediately, not
 * merely stop waiting for it. An aborted lookup ("aborted" status) is
 * the one outcome that is never retryable and never reported - the
 * session that wanted it is already gone, so spending a budget slot or
 * writing to whatever session is now current would both be wrong.
 *
 * CRITICAL ORDERING: `isStillCurrent()` is checked IMMEDIATELY after the
 * lookup resolves and BEFORE the save callable is ever invoked - not
 * only afterward, in the scheduler's own continuation. Even with the
 * abort signal wired up, there is an unavoidable gap between the fetch
 * settling and this check running, so this stays as defense-in-depth:
 * httpsCallable always authenticates with whichever account is
 * CURRENTLY signed in at the moment it is called, not whichever account
 * was signed in when the lookup started - without this check, a lookup
 * started for account A could be submitted and saved under account B's
 * (or a NEW session for account A's own) current authentication,
 * writing a country obtained for one session onto a different one.
 * `isStillCurrent()` reads the live generation state at call time (see
 * runCountryDetectionAttempt below), so it correctly catches every one
 * of: logout, account switch, logout-then-relogin to the SAME uid
 * (generation still changes even though uid returns to what it was),
 * and unmount.
 * @param {function(): boolean} isStillCurrent
 * @param {AbortSignal|undefined} signal
 * @return {Promise<{resolved: boolean, alreadyLocked: boolean,
 *   retryable: boolean}>} never rejects.
 */
async function performCountryDetectionAndReport(isStillCurrent, signal) {
  let lookup;
  try {
    // detectUserCountry() is documented to never reject - this try/catch
    // is defense-in-depth only, so a bug in that contract can never
    // produce an unhandled rejection here or surface as an error state
    // anywhere in the UI; treated as a transient, retryable outcome,
    // same as any other classified non-"ok" status.
    lookup = await detectUserCountry({signal});
  } catch {
    return {resolved: false, alreadyLocked: false, retryable: true};
  }

  if (lookup.status === "aborted") {
    // The scheduler itself cancelled this attempt (session boundary) -
    // never retryable, never reported. The scheduler's own generation/
    // mountedRef guards below would independently block a write too,
    // but this makes the "no report after abort" guarantee explicit and
    // structural, not merely incidental.
    return {resolved: false, alreadyLocked: false, retryable: false};
  }

  if (!isStillCurrent()) {
    // The session this lookup was for is no longer current - never
    // submit it under whatever account/session is now signed in, and
    // never retry on behalf of a session that no longer exists; the
    // scheduler's own stale-response guard discards this result
    // regardless, but returning retryable:false here means this
    // specific outcome never itself schedules anything either.
    return {resolved: false, alreadyLocked: false, retryable: false};
  }

  if (lookup.status !== "ok" || !lookup.country) {
    // EVERY unresolved status spends one of the bounded attempts - no
    // status is special-cased as "not worth retrying". See this
    // function's header and COUNTRY_RETRY_DELAYS_MS's comment above for
    // why: a transient network condition for one status can just as
    // easily be gone by the next backed-off attempt as for any other.
    return {resolved: false, alreadyLocked: false, retryable: true};
  }

  // A valid ISO2 code was obtained - persist it through the
  // authenticated callable. The uid is derived server-side from the
  // caller's own auth token, never sent from here.
  try {
    const functions = getFunctions(undefined, "us-central1");
    const reportClientCountry = httpsCallable(functions, "reportClientCountry");
    const res = await reportClientCountry({country: lookup.country});
    const data = (res && res.data) || {};
    return {
      resolved: data.saved === true,
      alreadyLocked: data.alreadyLocked === true,
      // The lookup itself already succeeded; a failure here is the
      // save call's own transient issue (network/infrastructure, or a
      // not-yet-created user document - see maybeStartCountryDetectionRetry
      // below for why detection can start before the doc exists) -
      // worth one more attempt within the existing bound rather than
      // giving up on an otherwise-successful lookup.
      retryable: data.saved !== true && data.alreadyLocked !== true,
    };
  } catch {
    return {resolved: false, alreadyLocked: false, retryable: true};
  }
}

export const ACCOUNT_ACCESS_UNKNOWN = "unknown";
export const ACCOUNT_ACCESS_ALLOWED = "allowed";
export const ACCOUNT_ACCESS_BLOCKED = "blocked";

export const ACCOUNT_REASON_NONE = "none";
export const ACCOUNT_REASON_PENDING_DELETE = "pending_delete";
export const ACCOUNT_REASON_SUSPENDED = "suspended";
export const ACCOUNT_REASON_NEEDS_GENDER = "needs_gender";
export const ACCOUNT_REASON_NEEDS_VERIFICATION = "needs_verification";
export const ACCOUNT_REASON_SIGNED_OUT = "signed_out";
export const ACCOUNT_REASON_ERROR = "error";

const GENDER_THEME_MALE = "male";
const GENDER_THEME_FEMALE = "female";
const GENDER_THEME_DEFAULT = "default";

const ALLOWED_FEMALE_VERIFICATION_STATUSES = [
  "approved",
  "provisionally_approved",
];

function initialStateFor(uid) {
  return {
    loading: true,
    access: ACCOUNT_ACCESS_UNKNOWN,
    reason: uid ? ACCOUNT_REASON_NONE : ACCOUNT_REASON_SIGNED_OUT,
    // Transient/reset value only - see GenderThemeContext.js for why the
    // DOM data-theme attribute (a separate, CSS-facing channel) keeps its
    // own original "male" default independent of this field.
    genderTheme: GENDER_THEME_DEFAULT,
  };
}

// The refresh() no-op fallback below only applies to a consumer rendered
// without an AccountSessionStateProvider ancestor (e.g. an isolated unit
// test) - in production this provider always wraps the whole route tree,
// so every real caller gets the Provider's actual refresh implementation.
const AccountSessionStateContext = createContext({
  ...initialStateFor(null),
  refresh: () => Promise.resolve(),
});

export function useAccountSessionState() {
  return useContext(AccountSessionStateContext);
}

export function AccountSessionStateProvider({ children }) {
  const { user, isReady } = useContext(AuthContext);
  const uid = user?.uid || null;
  const [state, setState] = useState(() => initialStateFor(uid));
  // Guards against a stale async resolution (for a UID that is no longer
  // current) overwriting a later UID's already-in-flight or resolved
  // state - compared against on every async continuation below.
  const currentUidRef = useRef(uid);
  // Not unmount-cancelled by a cleanup closure (see below) - a single
  // monotonically increasing id instead, so BOTH an unmount/uid-change AND
  // an explicit refresh() call for the SAME uid correctly invalidate any
  // earlier, still-in-flight resolution for that uid. Without this, two
  // overlapping resolutions for one uid (e.g. the mount effect and a
  // refresh() fired moments later) could apply out of order - whichever
  // resolve() happened to finish last would win, even if it was started
  // first and is therefore the staler of the two.
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  // Session-boundary counter - bumped every time currentUidRef actually
  // changes (sign-out, sign-in, or switching accounts; see the effect
  // below), INCLUDING a logout immediately followed by a login to the
  // SAME uid. Comparing against THIS, not uid alone, is what the retry-
  // chain staleness checks below use: a uid-only comparison cannot tell
  // "this response belongs to the session that is still current" apart
  // from "this response belongs to an EARLIER session for a uid that
  // has since signed back in" - both have the same uid value.
  // Generation has no such ambiguity, since it strictly increases
  // across every session boundary.
  const sessionGenerationRef = useRef(0);
  // Per-uid, in-memory, session-scoped bounded-retry scheduler state:
  // {uid, generation, attempt, timer, inFlight, controller}. `uid` marks
  // which uid the current chain (if any) belongs to, so a second
  // resolveAccountState pass for the SAME uid within the SAME session
  // (mount, onboarding-completion refresh(), a later out-of-band
  // refresh(), or the AccountErrorRecovery retry button) never starts a
  // second, overlapping chain - see maybeStartCountryDetectionRetry
  // below. `generation` pins this chain to the session that started it -
  // see runCountryDetectionAttempt's staleness checks. `controller` is
  // the AbortController for whichever attempt is currently in flight (or
  // null when none is) - aborted immediately on unmount/uid-change below
  // so a logout/account-switch cancels the actual network request, not
  // merely the scheduler's own bookkeeping. Reset (and any pending timer/
  // in-flight request cancelled) whenever the uid changes - including
  // through signed-out - in the effect below, so a genuinely NEW
  // session, even for the same returning uid, gets its own fresh
  // 4-attempt budget AND its own generation.
  const countryRetryRef = useRef({
    uid: null, generation: 0, attempt: 0, timer: null, inFlight: false,
    controller: null,
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Cancel on unmount: this provider is mounted once for the app's
      // lifetime (see the file header), so this is a defensive, not a
      // normally-exercised, path - but a pending backed-off timer must
      // never fire after the provider that owns it is gone, and an
      // in-flight fetch must be aborted immediately, not merely ignored
      // once it eventually settles.
      if (countryRetryRef.current.timer) {
        clearTimeout(countryRetryRef.current.timer);
      }
      if (countryRetryRef.current.controller) {
        countryRetryRef.current.controller.abort();
      }
    };
  }, []);

  // Fires one attempt. Schedules the next one (if any budget remains
  // and the outcome was retryable) at the fixed 5s/15s/45s schedule -
  // stopping on success, an already-locked country, a non-retryable
  // outcome, or exhausting the bound. `generation` is captured at
  // schedule time and re-checked against the CURRENT
  // countryRetryRef.current.generation (never against a possibly-stale
  // local snapshot, which would trivially always match its own captured
  // value) after the (unavoidably un-abortable, from the scheduler's
  // point of view) lookup+save settles - this is the generation guard: a
  // sign-out followed by a sign-in to the SAME uid bumps the generation
  // even though the uid value returns to what it was, so a late response
  // from the PRIOR session is correctly recognized as stale even though
  // currentUidRef.current matches targetUid again.
  const runCountryDetectionAttempt = useCallback((targetUid, generation) => {
    if (countryRetryRef.current.generation !== generation) return;
    const retryState = countryRetryRef.current;
    if (retryState.inFlight) return; // overlap guard
    retryState.inFlight = true;
    // Owned by THIS attempt - aborted directly by the unmount/uid-change
    // effects below (via countryRetryRef.current.controller), which lets
    // a session boundary cancel the actual in-flight fetch immediately,
    // not merely stop waiting for it. Cleared from the ref once this
    // attempt settles (success, failure, or abort) so a later effect
    // never aborts a controller that no longer corresponds to anything
    // in flight.
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    retryState.controller = controller;
    // Passed into performCountryDetectionAndReport so it can check
    // staleness itself, right after the lookup and before the save
    // call - see that function's header for why checking only here,
    // after everything (including the save) has already happened, is
    // too late.
    const isStillCurrent = () =>
      mountedRef.current && countryRetryRef.current.generation === generation;
    performCountryDetectionAndReport(
        isStillCurrent, controller ? controller.signal : undefined,
    ).then((result) => {
      retryState.inFlight = false;
      retryState.controller = null;
      // Stale guard: generation (see above) covers sign-out/sign-in
      // (including back to the same uid); mountedRef covers a plain
      // unmount with the same session still otherwise current - without
      // the latter, a call still in flight when the provider unmounts
      // could schedule a timer after its own cleanup effect already ran
      // (and so never got a chance to cancel it).
      if (
        !mountedRef.current ||
        countryRetryRef.current.generation !== generation
      ) {
        return;
      }
      if (result.resolved || result.alreadyLocked || !result.retryable) {
        return; // stop: success, already locked, or non-retryable (abort)
      }
      if (retryState.attempt >= COUNTRY_RETRY_DELAYS_MS.length) return; // bound
      const delayMs = COUNTRY_RETRY_DELAYS_MS[retryState.attempt];
      retryState.attempt += 1;
      retryState.timer = setTimeout(() => {
        retryState.timer = null;
        if (
          !mountedRef.current ||
          countryRetryRef.current.generation !== generation
        ) {
          return; // stale guard
        }
        runCountryDetectionAttempt(targetUid, generation);
      }, delayMs);
    });
  }, []);

  // Entry point called from resolveAccountState below, every pass, for
  // any uid with no locked country yet - starts the bounded chain ONLY
  // on the first such call for a given uid this session; every later
  // call for the SAME uid (a manual refresh(), a navigation, the
  // AccountErrorRecovery retry button) is a deliberate no-op, so it
  // never restarts the budget or creates a second, overlapping chain.
  // A genuinely NEW session for the same returning uid (see the
  // uid-change effect below, which resets countryRetryRef.current.uid
  // to null and bumps the generation) gets its own fresh call here and
  // so its own fresh 4-attempt budget - this is what satisfies "retry
  // again in a later application session if country is still missing".
  const maybeStartCountryDetectionRetry = useCallback((targetUid) => {
    if (countryRetryRef.current.uid === targetUid) return;
    if (countryRetryRef.current.timer) clearTimeout(countryRetryRef.current.timer);
    if (countryRetryRef.current.controller) {
      countryRetryRef.current.controller.abort();
    }
    const generation = sessionGenerationRef.current;
    countryRetryRef.current = {
      uid: targetUid, generation, attempt: 0, timer: null, inFlight: false,
      controller: null,
    };
    runCountryDetectionAttempt(targetUid, generation);
  }, [runCountryDetectionAttempt]);

  // Extracted from the mount effect so refresh() (below) can invoke the
  // exact same resolution logic on demand, against the exact same
  // requestId/applyIfCurrent guard - not a second, parallel implementation
  // of the same accountStatus/gender/verification checks.
  const resolveAccountState = useCallback((resolvedForUid, authUser, requestId) => {
    const applyIfCurrent = (next) => {
      if (
        !mountedRef.current ||
        requestIdRef.current !== requestId ||
        currentUidRef.current !== resolvedForUid
      ) {
        return;
      }
      setState(next);
    };

    return (async () => {
      try {
        const snap = await getDoc(doc(db, "users", resolvedForUid));

        if (!snap.exists()) {
          // Country detection starts even before ensureUserProfile has
          // created this user's document - a UID is all it needs (see
          // maybeStartCountryDetectionRetry/performCountryDetectionAndReport
          // above: reportClientCountry safely no-ops, as "retryable",
          // until the doc exists, and the bounded 5s/15s/45s schedule
          // naturally covers that window without creating anything
          // itself). Never awaited, never affects this blocked/
          // needs_gender resolution below.
          maybeStartCountryDetectionRetry(resolvedForUid);
          applyIfCurrent({
            loading: false,
            access: ACCOUNT_ACCESS_BLOCKED,
            reason: ACCOUNT_REASON_NEEDS_GENDER,
            genderTheme: GENDER_THEME_MALE,
          });
          return;
        }

        const data = snap.data() || {};

        // Started as early as possible - BEFORE the pending_delete/
        // suspended/needs_gender gates below, so detection runs
        // throughout the ENTIRE incomplete-onboarding window (an
        // authenticated user with a document but no gender set yet),
        // not only after onboarding completes. Never awaited, never
        // blocking the access/theme resolution below (here or further
        // down); a no-op if a chain for this uid is already running or
        // already exhausted this session - see
        // maybeStartCountryDetectionRetry above.
        if (!data.realCountry) {
          maybeStartCountryDetectionRetry(resolvedForUid);
        }

        if (data.accountStatus === "pending_delete") {
          applyIfCurrent({
            loading: false,
            access: ACCOUNT_ACCESS_BLOCKED,
            reason: ACCOUNT_REASON_PENDING_DELETE,
            genderTheme: data.gender === "female" ? GENDER_THEME_FEMALE : GENDER_THEME_MALE,
          });
          return;
        }

        if (data.accountStatus === "suspended") {
          applyIfCurrent({
            loading: false,
            access: ACCOUNT_ACCESS_BLOCKED,
            reason: ACCOUNT_REASON_SUSPENDED,
            genderTheme: data.gender === "female" ? GENDER_THEME_FEMALE : GENDER_THEME_MALE,
          });
          return;
        }

        if (!data.gender) {
          applyIfCurrent({
            loading: false,
            access: ACCOUNT_ACCESS_BLOCKED,
            reason: ACCOUNT_REASON_NEEDS_GENDER,
            genderTheme: GENDER_THEME_MALE,
          });
          return;
        }

        const resolvedTheme = data.gender === "female" ? GENDER_THEME_FEMALE : GENDER_THEME_MALE;

        const tokenResult = await getIdTokenResult(authUser);
        if (requestIdRef.current !== requestId || currentUidRef.current !== resolvedForUid) {
          return;
        }
        const isDeveloper = tokenResult?.claims?.developer === true;

        if (isDeveloper) {
          applyIfCurrent({
            loading: false,
            access: ACCOUNT_ACCESS_ALLOWED,
            reason: ACCOUNT_REASON_NONE,
            genderTheme: resolvedTheme,
          });
          return;
        }

        const verificationStatus = String(data.verificationStatus || "none");

        if (
          data.gender === "female" &&
          !ALLOWED_FEMALE_VERIFICATION_STATUSES.includes(verificationStatus)
        ) {
          applyIfCurrent({
            loading: false,
            access: ACCOUNT_ACCESS_BLOCKED,
            reason: ACCOUNT_REASON_NEEDS_VERIFICATION,
            genderTheme: resolvedTheme,
          });
          return;
        }

        applyIfCurrent({
          loading: false,
          access: ACCOUNT_ACCESS_ALLOWED,
          reason: ACCOUNT_REASON_NONE,
          genderTheme: resolvedTheme,
        });
      } catch (err) {
        // Must never leave loading true forever - RequireGender.js's own
        // prior check() had no catch at all, so a thrown read left the
        // guarded route blank indefinitely. This resolves safely instead:
        // access stays blocked (never a false "allowed"), never hangs.
        console.error("AccountSessionState resolve error:", err);
        applyIfCurrent({
          loading: false,
          access: ACCOUNT_ACCESS_BLOCKED,
          reason: ACCOUNT_REASON_ERROR,
          genderTheme: GENDER_THEME_MALE,
        });
      }
    })();
  }, [maybeStartCountryDetectionRetry]);

  useEffect(() => {
    // A genuinely new uid (including a transition through signed-out)
    // starts a new app session for the retry budget above - even a
    // returning uid that signs back in gets its own fresh 4-attempt
    // budget, never inheriting a prior session's progress. Bump the
    // generation counter on every such transition (see
    // sessionGenerationRef above) - this, not the uid comparison here,
    // is what runCountryDetectionAttempt's staleness checks rely on,
    // precisely because a logout immediately followed by a login to the
    // SAME uid is ALSO a transition here (currentUidRef.current !== uid
    // is true going to null, and true again coming back), ensuring a
    // response/timer from the OLD session can never be mistaken for
    // belonging to the NEW one even though their uid values are
    // identical. Also cancel any pending backed-off timer AND abort any
    // in-flight fetch from the previous uid's chain - a sign-out/
    // account-change must never let a stale timer fire into a different
    // session, or let an in-flight request for the OLD session keep
    // running only to be silently discarded once it eventually settles;
    // it is cancelled immediately instead (generation is still a
    // separate, independent guard for the rare case a response is
    // already past the abort-check point when this runs).
    if (currentUidRef.current !== uid) {
      sessionGenerationRef.current += 1;
      if (countryRetryRef.current.timer) {
        clearTimeout(countryRetryRef.current.timer);
      }
      if (countryRetryRef.current.controller) {
        countryRetryRef.current.controller.abort();
      }
      countryRetryRef.current = {
        uid: null, generation: sessionGenerationRef.current, attempt: 0,
        timer: null, inFlight: false, controller: null,
      };
    }
    currentUidRef.current = uid;
    // A new request id invalidates any earlier in-flight resolution for
    // the previous uid (or an earlier refresh() for the same uid) - see
    // resolveAccountState's applyIfCurrent guard above.
    const requestId = ++requestIdRef.current;

    if (!isReady) {
      setState({
        loading: true,
        access: ACCOUNT_ACCESS_UNKNOWN,
        reason: ACCOUNT_REASON_NONE,
        genderTheme: GENDER_THEME_DEFAULT,
      });
      return;
    }

    // Immediate, synchronous reset BEFORE any async work starts - a
    // stale-but-still-rendering guarded page must never keep showing the
    // PREVIOUS account's access/theme for even one extra frame.
    setState(initialStateFor(uid));

    if (!uid) {
      setState({
        loading: false,
        access: ACCOUNT_ACCESS_BLOCKED,
        reason: ACCOUNT_REASON_SIGNED_OUT,
        genderTheme: GENDER_THEME_DEFAULT,
      });
      return;
    }

    resolveAccountState(uid, user, requestId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, uid]);

  // Explicit, lifecycle-safe refresh/invalidation entry point: callers
  // that just performed a known mutation of users/{uid} (onboarding
  // completion is the first caller - see SelectGenderPage.js) can await
  // this to re-resolve the authoritative access/reason BEFORE navigating,
  // instead of relying on the next unrelated [isReady, uid] change (which
  // never happens for an in-session mutation) or a full page reload. Does
  // NOT create a second source of truth: it re-runs the exact same
  // resolveAccountState used by the mount effect, against the current uid
  // only - a no-op (resolved immediately) while signed out or not ready.
  const refresh = useCallback(() => {
    if (!isReady || !uid) return Promise.resolve();
    const requestId = ++requestIdRef.current;
    return resolveAccountState(uid, user, requestId);
  }, [isReady, uid, user, resolveAccountState]);

  // Memoized so consumers only re-render when the resolved state or the
  // refresh function identity actually changes - not on every provider
  // render (e.g. a parent re-render that doesn't touch isReady/uid/state).
  const contextValue = useMemo(() => ({ ...state, refresh }), [state, refresh]);

  return (
    <AccountSessionStateContext.Provider value={contextValue}>
      {children}
    </AccountSessionStateContext.Provider>
  );
}
